import * as React from 'react';
import { cn } from '@/lib/utils';

export type InputProps = React.ComponentPropsWithRef<'input'>;

function Input({ className, type, id, name, ref, ...props }: InputProps) {
  const generatedId = React.useId();
  // Callers that already wire an explicit id (either directly, or via <FormControl>
  // injecting formItemId) are assumed to carry their own <label htmlFor>/<FormLabel>.
  // Only standalone fields without an explicit id (search boxes, inline filters) get
  // an auto id + a placeholder-derived aria-label so they still resolve to an
  // accessible name and satisfy the "id or name" / "associated label" a11y checks.
  const resolvedId = id ?? generatedId;
  const hasAccessibleName = Boolean(props['aria-label'] || props['aria-labelledby']);
  const fallbackAriaLabel =
    id === undefined && !hasAccessibleName && props.placeholder
      ? { 'aria-label': props.placeholder }
      : undefined;

  return (
    <input
      type={type}
      id={resolvedId}
      name={name ?? resolvedId}
      className={cn(
        'flex h-9 w-full rounded-lg border border-input bg-card px-3 py-1 text-sm transition-colors placeholder:text-muted-foreground hover:border-ring/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      ref={ref}
      {...fallbackAriaLabel}
      {...props}
    />
  );
}
Input.displayName = 'Input';

export { Input };
